import { useState, useRef, useCallback, useEffect } from 'react';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

/**
 * useDictation — Speech-to-text hook designed for TipTap rich text editors.
 *
 * Captures speech via the Web Speech API and inserts the final transcript
 * into a TipTap editor at the current cursor position, or appends to the end.
 *
 * @param {object}  opts
 * @param {string}  opts.lang       – BCP-47 language tag (default 'en-US')
 * @param {object}  opts.editorRef  – React ref whose `.current` is the TipTap editor instance
 * @returns {{ status, transcript, isSupported, start, stop, cancel }}
 *
 * status: 'idle' | 'recording' | 'processing' | 'error'
 */
export default function useDictation({ lang = 'en-US', editorRef } = {}) {
  const isSupported = !!SpeechRecognition;

  const [status, setStatus] = useState('idle');       // idle | recording | processing | error
  const [transcript, setTranscript] = useState('');    // live preview of what's been said
  const [errorMsg, setErrorMsg] = useState(null);

  const recognitionRef = useRef(null);
  const finalBufferRef = useRef('');

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch { /* noop */ }
      }
    };
  }, []);

  /**
   * Insert text into TipTap editor at cursor or append to end.
   */
  const insertIntoEditor = useCallback((text) => {
    const editor = editorRef?.current;
    if (!editor || !text) return;

    const trimmed = text.trim();
    if (!trimmed) return;

    // Check if editor has a valid selection / is focused
    const { from, to } = editor.state.selection;
    const docSize = editor.state.doc.content.size;
    const isEmpty = editor.isEmpty;

    if (isEmpty) {
      // Editor is empty — just set content
      editor.chain().focus().insertContent(trimmed).run();
    } else if (editor.isFocused && from !== undefined) {
      // Cursor is active inside the editor — insert at position
      // Add a space before if the char before cursor isn't whitespace
      const charBefore = from > 0 ? editor.state.doc.textBetween(from - 1, from) : '';
      const spacer = charBefore && !/\s/.test(charBefore) ? ' ' : '';
      editor.chain().focus().insertContentAt({ from, to }, spacer + trimmed).run();
    } else {
      // No active cursor — append to end with paragraph break
      editor.chain().focus('end').insertContent('\n\n' + trimmed).run();
    }
  }, [editorRef]);

  const start = useCallback(() => {
    if (!isSupported) {
      setStatus('error');
      setErrorMsg('Voice dictation is not supported in this browser');
      return;
    }

    setErrorMsg(null);
    setTranscript('');
    finalBufferRef.current = '';

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;

    recognition.onresult = (event) => {
      let interim = '';
      let newFinals = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          newFinals += t;
        } else {
          interim += t;
        }
      }

      if (newFinals) {
        finalBufferRef.current += newFinals;
      }

      // Show live preview
      setTranscript(finalBufferRef.current + interim);
    };

    recognition.onerror = (event) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setStatus('error');
        setErrorMsg('Microphone access denied. Please allow microphone permissions.');
      } else if (event.error !== 'aborted') {
        setStatus('error');
        setErrorMsg('Dictation error: ' + event.error);
      }
    };

    recognition.onend = () => {
      const final = finalBufferRef.current.trim();
      if (final) {
        setStatus('processing');
        // Insert into editor
        insertIntoEditor(final);
        setTranscript(final);
        // Brief processing state then done
        setTimeout(() => setStatus('idle'), 600);
      } else {
        setStatus('idle');
      }
      setTranscript('');
    };

    recognitionRef.current = recognition;
    recognition.start();
    setStatus('recording');
  }, [isSupported, lang, insertIntoEditor]);

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* noop */ }
    }
    // status transitions handled in onend
  }, []);

  const cancel = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch { /* noop */ }
    }
    finalBufferRef.current = '';
    setTranscript('');
    setStatus('idle');
  }, []);

  return { status, transcript, errorMsg, isSupported, start, stop, cancel };
}
